import {
  VideosGetOrderByCreationTimeEnum,
  VideosGetOrderByDurationEnum,
  VideosGetOrderByNameEnum,
  VideosGetOrderByStartTimeEnum,
  VideosGetOrderByStateEnum,
  VideosGetOrderByTimeEnum,
  VideosGetRequest,
} from '@nativewaves/nw-cloud-sdk/content'
import { createRoute } from '@tanstack/react-router'
import { t } from 'i18next'

import { StaticRouteParameter } from 'config/routing'
import { Overview } from 'domains/Workspaces/Video/Objects'
import { videoRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video'
import { Structure } from 'pages/NWPlatform/Workspaces/Workspace/Video/Structure'

export const videoObjectsRoute = createRoute({
  getParentRoute: () => videoRoute,
  path: StaticRouteParameter.OBJECTS,
  loader: () => ({
    crumb: t('entity:content.videoObject', { count: 2 }),
  }),
})

export const videoObjectsLayoutRoute = createRoute({
  getParentRoute: () => videoObjectsRoute,
  id: 'videoObjectLayout',
  component: Structure,
})

const sortProperties = [
  'orderByTime',
  'orderByName',
  'orderByDuration',
  'orderByState',
  'orderByStartTime',
  'orderByCreationTime',
] as const satisfies (keyof PickStartsWith<VideosGetRequest, 'orderBy'>)[]

const sortPropertyValues = new Map<keyof PickStartsWith<VideosGetRequest, 'orderBy'>, Record<string, string>>([
  ['orderByTime', VideosGetOrderByTimeEnum],
  ['orderByName', VideosGetOrderByNameEnum],
  ['orderByDuration', VideosGetOrderByDurationEnum],
  ['orderByState', VideosGetOrderByStateEnum],
  ['orderByStartTime', VideosGetOrderByStartTimeEnum],
  ['orderByCreationTime', VideosGetOrderByCreationTimeEnum],
])

type VideoObjectsOverviewSearch = Pick<VideosGetRequest, 'groups' | 'excludeGrouped' | 'query'> &
  Partial<PickStartsWith<VideosGetRequest, 'timeRange'>> &
  Partial<PickStartsWith<VideosGetRequest, 'orderBy'>>

export const videoObjectsOverviewRoute = createRoute({
  getParentRoute: () => videoObjectsLayoutRoute,
  path: '/',
  component: () => <Overview />,
  validateSearch: (search: Record<string, unknown>): VideoObjectsOverviewSearch => {
    const result: Record<string, unknown> = {}

    if (search.query && typeof search.query === 'string' && !!search.query.length) {
      result.query = search.query
    }

    if (search.groups && typeof search.groups === 'string' && !!search.groups.length) {
      result.groups = search.groups
    }

    if (search.excludeGrouped && typeof search.excludeGrouped === 'string' && !!search.excludeGrouped.length) {
      result.excludeGrouped = search.excludeGrouped
    }

    if (search.timeRangeStart && typeof search.timeRangeStart === 'number' && !isNaN(search.timeRangeStart)) {
      result.timeRangeStart = search.timeRangeStart
    }

    if (search.timeRangeEnd && typeof search.timeRangeEnd === 'number' && !isNaN(search.timeRangeEnd)) {
      result.timeRangeEnd = search.timeRangeEnd
    }

    for (const sortProperty of sortProperties) {
      if (
        search[sortProperty] &&
        typeof search[sortProperty] === 'string' &&
        search[sortProperty] in sortPropertyValues.get(sortProperty)!
      ) {
        result[sortProperty] = search[sortProperty]
      }
    }

    return result satisfies VideoObjectsOverviewSearch
  },
})
